import { AxiosResponse } from 'axios';
import axiosInstance from './axios';

export type CorsiaPhase = { key: string; label: string };

/** One standard/methodology/version combination and its per-phase eligibility. */
export type CorsiaMethodologyEntry = {
  id: string;
  standard: string;
  methodology: string;
  versions: string[];
  /** Defaults to Pilot / Phase 1 / Phase 2; extra phases can be added. */
  phases?: CorsiaPhase[];
  /** TipTap document per phase key. */
  eligibility: Record<string, any>;
  /** ISO timestamp set when the entry is first saved. */
  created_at?: string;
};

/** The lookup is grouped by project type, mirroring how it is browsed. */
export type CorsiaLookupRow = {
  id: string;
  project_type: string;
  methodologies: CorsiaMethodologyEntry[];
};

export type CorsiaParticipationRow = {
  country: string;
  phase: string;
  status: string;
};

export type CorsiaCatcherData = {
  eligibility_lookup: { data: CorsiaLookupRow[]; seeded_at?: string };
  participation: { data: CorsiaParticipationRow[] };
};

export type CorsiaCatcherResponse = {
  id?: string;
  json_data?: CorsiaCatcherData;
  workflow_state?: 'staging' | 'production';
  created_at?: string;
  updated_at?: string;
};

const CORSIA_PATH = '/research-hub/corsia';

export const getCorsiaCatcher = async (): Promise<CorsiaCatcherResponse> => {
  try {
    const response: AxiosResponse = await axiosInstance.get(`${CORSIA_PATH}/catcher`);
    return response.data;
  } catch (error) {
    throw new Error(`Error fetching CORSIA catcher: ${error}`);
  }
};

export const updateCorsiaCatcher = async (data: { json_data: CorsiaCatcherData }): Promise<any> => {
  try {
    const response: AxiosResponse = await axiosInstance.put(CORSIA_PATH, data);
    return response.data;
  } catch (error) {
    throw new Error(`Error saving CORSIA catcher: ${error}`);
  }
};

export const updateCorsiaWorkflow = async (payload: {
  state: 'staging' | 'production';
}): Promise<any> => {
  try {
    const response: AxiosResponse = await axiosInstance.put(`${CORSIA_PATH}/workflow`, payload);
    return response.data;
  } catch (error: any) {
    const errorMessage = error.response?.data?.message || 'internal service error';
    throw new Error(`Error updating CORSIA workflow state: ${errorMessage}`);
  }
};
